/*
 * URLs listed as released are opened in the SPA
 * Unreleased URLs are turned into an anchor link
 *
 * IMPORTANT: For each route, also add the respective page regex
 */

import { ROUTE_PAGE } from '@/router'
import type { App } from 'vue'
import { inject } from 'vue'
import type { RouteLocation } from 'vue-router'

export type ReleasedUrlsSettings = {
    releasedRoutes: string[]
    releasedPages: RegExp[]
}

export type ReleasedUrlsChecker = (url: RouteLocation) => boolean

export function installReleasedUrls(
    app: App,
    config: ReleasedUrlsSettings,
    host: URL
): ReleasedUrlsChecker {
    const isReleasedUrl = createReleasedUrls(config, host)

    app.provide('_is-released-url', isReleasedUrl)

    return isReleasedUrl
}

export function createReleasedUrls(
    config: ReleasedUrlsSettings,
    host: URL
): ReleasedUrlsChecker {
    const isLocal =
        (import.meta.env.MODE === 'development' &&
            !Number(import.meta.env.VITE_USE_RELEASED_URLS_PLUGIN_IN_DEV)) ||
        host.hostname === 'staging-frontend.coderings.com' ||
        host.hostname === 'fe2-prod.coderings.com' ||
        host.hostname === 'thlocal.local' ||
        host.hostname === 'thlocal' ||
        host.hostname === 'proxy' ||
        host.hostname === '127.0.0.1'

    if (isLocal) {
        return () => true
    }

    return (to: RouteLocation): boolean => {
        const name = to.name

        for (let i = 0; i < config.releasedRoutes.length; i += 1) {
            if (config.releasedRoutes[i] === name) {
                return true
            }
        }

        if (ROUTE_PAGE === name) {
            const path = to.path

            for (const urlRegex of config.releasedPages) {
                if (urlRegex.test(path)) {
                    return true
                }
            }
        }

        return false
    }
}

export function useReleasedUrls(): ReleasedUrlsChecker {
    return inject('_is-released-url') as ReleasedUrlsChecker
}
